import { useMemo, useCallback, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import Loading from '@/components/general/Loading';
import candidatePagesContext from '@/contexts/candidatePagesContext';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import useCandidateStatuses from '@/hooks/useCandidateStatuses';
import candidatesServices, {
  AttachCandidateAndTechStackType,
  EditableCandidate,
  CandidateParams,
  CandidateResponse,
  CandidateProfileResponse,
} from '@/services/candidates';
import technologiesServices from '@/services/technologies';
import usersApi from '@/services/user';
import { AvailableRoles, FetchApiIds, QuestionType, TechStackType } from '@/types';
import { ResponseApiAllUsers } from '@/types/api';
import { getInputType } from '@/utils/candidate';
import { onScrollToSmoothly } from '@/utils/global';
import CandidateInfoComponent from './CandidateInfo.component';
import {
  dropDownInputs,
  dropdownOptionsMap,
  fieldsAndFriendlyLabels,
  allSelectedFields,
} from './candidateInfoFields';

const CandidateInfoPage = function (): JSX.Element {
  const { id } = useParams();
  const candidateStatuses = useCandidateStatuses();
  const [updatedField, setUpdatedField] = useState<string>();
  const toast = useToastContext();
  const { refetchCandidate, candidate, isLoadingCandidate } = useContext(candidatePagesContext);

  const dropdownOptionsMapOverwritten = useMemo(() => {
    return {
      ...dropdownOptionsMap,
      status: candidateStatuses,
    };
  }, [candidateStatuses]);

  const candidateProfileResponse = useApiFetch<CandidateProfileResponse>(
    [FetchApiIds.CANDIDATE_PROFILE, id],
    async () => candidatesServices.getProfileByID(id as string)
  );

  const allUsersResponse = useApiFetch<ResponseApiAllUsers>(FetchApiIds.GET_ALL_UERS, async () =>
    usersApi.getAllUsers({
      page: 1,
      take: 999,
    })
  );

  const candidateOwnersOptions = useMemo(() => {
    return (allUsersResponse.data?.users.data ?? [])
      .filter((user) => {
        return user.roles?.some((role) => role.value === AvailableRoles.HIRING_ADMIN);
      })
      .map((value) => ({
        label: value?.name ?? '',
        value: value?.id,
      }));
  }, [allUsersResponse.data]);

  const updatedLinkCandidateAndTechIdMutation = useApiSubmit<void, AttachCandidateAndTechStackType>(
    candidatesServices.updatedLinkCandidateAndTechId
  );

  const allTechStacksResponse = useApiFetch<TechStackType[], void>(
    FetchApiIds.ALL_TECHS,
    technologiesServices.getAllTechnologies,
    {
      retry: 2,
    }
  );

  const techStacksOptions = useMemo(() => {
    return (allTechStacksResponse.data ?? []).map((tech) => ({
      label: tech?.name,
      value: tech?.id,
    }));
  }, [allTechStacksResponse.data]);

  const updateCandidateMutation = useApiSubmit<CandidateResponse, CandidateParams>(
    candidatesServices.updateCandidateById
  );

  const workExperiences = useMemo(() => {
    return candidateProfileResponse?.data?.profile.workExperiences ?? [];
  }, [candidateProfileResponse?.data?.profile.workExperiences]);

  const expertises = useMemo(() => {
    return candidateProfileResponse?.data?.profile.expertises ?? [];
  }, [candidateProfileResponse?.data?.profile.expertises]);

  const candidateEducation = useMemo(() => {
    return candidateProfileResponse?.data?.profile.educations ?? [];
  }, [candidateProfileResponse?.data?.profile.educations]);

  const onSubmitHandler = async (updatedCandidate: EditableCandidate): Promise<void> => {
    if (!updatedField) {
      return;
    }

    toast.setMessage('Saving information...');
    toast.setSeverity('info');
    toast.showToast();

    try {
      let newValue = updatedCandidate[updatedField];
      if (!!newValue && typeof newValue === 'string') {
        newValue = newValue.trim();
      }

      const isTrioOrGithubUrl = ['githubUrl', 'trioProfileUrl'].includes(updatedField);
      const isTechStack = updatedField === 'skills';
      const isOwnerField = updatedField === 'candidateOwner';
      if (isTechStack) {
        const { skills } = updatedCandidate;

        await updatedLinkCandidateAndTechIdMutation.mutateAsync({
          candidateId: id as string,
          techs: skills as string[],
        });
      }

      if (isTrioOrGithubUrl) {
        const params = {
          candidate: {
            id: updatedCandidate.id,
            [updatedField]: newValue || null,
          },
        };

        await updateCandidateMutation.mutateAsync(params);
      }

      if (isOwnerField) {
        const params = {
          candidate: {
            id: updatedCandidate.id,
            ownerId: newValue as string,
          },
        };

        await updateCandidateMutation.mutateAsync(params);
      }

      if (!isTechStack && !isOwnerField && !isTrioOrGithubUrl) {
        const params = {
          candidate: {
            id: updatedCandidate.id,
            [updatedField]: newValue,
          },
        };

        await updateCandidateMutation.mutateAsync(params);
      }

      refetchCandidate();

      toast.setMessage('The candidate information has been updated.');
      toast.setSeverity('success');
      toast.showToast();
    } catch (err) {
      toast.setMessage('There was an error saving the data.');
      toast.setSeverity('error');
      toast.showToast();
    }
  };

  const onSubmitFromResume = async (updatedCandidate: EditableCandidate): Promise<void> => {
    toast.setMessage('Saving information...');
    toast.setSeverity('info');
    toast.showToast();

    const params = {
      candidate: {
        id: updatedCandidate.id,
        firstName: updatedCandidate.firstName,
        lastName: updatedCandidate.lastName,
        email: updatedCandidate.email,
        mobilePhone: updatedCandidate.mobilePhone,
        country: updatedCandidate.country,
        city: updatedCandidate.city,
        developerType: updatedCandidate.developerType,
        experience: updatedCandidate.experience,
        englishLevel: updatedCandidate.englishLevel?.toUpperCase(),
        salaryExpectation: updatedCandidate.salaryExpectation,
        linkedinUrl: updatedCandidate.linkedinUrl,
        githubUrl: updatedCandidate.githubUrl,
      },
    };

    try {
      await updateCandidateMutation.mutateAsync(params);

      toast.setMessage('The candidate information has been updated.');
      toast.setSeverity('success');
      toast.showToast();
    } catch (err) {
      toast.setMessage('There was an error saving the data.');
      toast.setSeverity('error');
      toast.showToast();
    } finally {
      refetchCandidate();
    }
  };

  const buildDropdowns = useCallback(
    (key) => {
      const isDropdown = dropDownInputs.includes(key);
      const isTechStacks = key === 'skills';

      if (isDropdown && !isTechStacks) {
        return {
          options: dropdownOptionsMapOverwritten[key],
        };
      }
      return {};
    },
    [dropdownOptionsMapOverwritten]
  );

  const buildGroupedDropdowns = useCallback(
    (key) => {
      const hasOptionsGrouped = key === 'status';
      const dropdownOptions = dropdownOptionsMapOverwritten[key];
      if (hasOptionsGrouped) {
        return {
          dropdownGroups: dropdownOptions,
        };
      }

      return {};
    },
    [dropdownOptionsMapOverwritten]
  );

  const buildFriendlyDropdown = useCallback(
    (key) => {
      const isEnglish = key === 'englishLevel';
      const isTalentPool = key === 'isTalentPool';
      const isTechStacks = key === 'skills';
      const dropdownOptions = isTechStacks ? techStacksOptions : dropdownOptionsMapOverwritten[key];

      if (key === 'candidateOwner') {
        return {
          optionsWithLabel: (candidateOwnersOptions || []).sort((a, b) =>
            a.label.localeCompare(b.label)
          ),
        };
      }

      if (isEnglish || isTechStacks || isTalentPool) {
        return {
          isMultiple: isTechStacks,
          optionsWithLabel: dropdownOptions,
        };
      }

      return {};
    },
    [candidateOwnersOptions, dropdownOptionsMapOverwritten, techStacksOptions]
  );

  const buildCommonFields = useCallback((key, value) => {
    const label = fieldsAndFriendlyLabels[key] ?? key;
    const type = getInputType(key);
    const isDisabled = type === QuestionType.rating || key === 'id';

    return {
      name: key,
      label,
      isDisabled,
      type,
      value,
    };
  }, []);

  const formatQuestionObject = useCallback(
    ([key, value]): any => {
      return {
        ...buildCommonFields(key, value),
        ...buildDropdowns(key),
        ...buildGroupedDropdowns(key),
        ...buildFriendlyDropdown(key),
      };
    },
    [buildDropdowns, buildGroupedDropdowns, buildFriendlyDropdown, buildCommonFields]
  );

  const formattedQuestions = useMemo(() => {
    return Object.entries(candidate || {})
      .filter(([key]) => allSelectedFields.includes(key))
      .map(formatQuestionObject);
  }, [candidate, formatQuestionObject]);

  const formattedCandidate = useMemo(() => {
    let valuesToSubmit = { skills: [], candidateOwner: null };

    formattedQuestions.forEach(({ name, value }) => {
      const isTechStack = name === 'skills';
      const isOwner = name === 'candidateOwner';
      if (isOwner) {
        const ownerId = candidateOwnersOptions.find((option) => option.label === value)?.value;
        valuesToSubmit = {
          ...valuesToSubmit,
          [name]: ownerId ?? '',
        };
        return;
      }
      if (isTechStack) {
        const mainStacks = value.filter((tech) => tech?.isMainStack);
        const techStacksFormatted = mainStacks.map((tech) => tech?.skill?.id);
        valuesToSubmit = {
          ...valuesToSubmit,
          [name]: techStacksFormatted,
        };
        return;
      }

      valuesToSubmit = {
        ...valuesToSubmit,
        [name]: value ?? '',
      };
    });

    return valuesToSubmit;
  }, [candidateOwnersOptions, formattedQuestions]);

  const onSaveText = async (value: string): Promise<void> => {
    try {
      const params = {
        candidate: {
          summary: value || null,
          id,
        },
      };
      await updateCandidateMutation.mutateAsync(params);
      refetchCandidate();

      toast.setMessage('The candidate information has been updated.');
      toast.setSeverity('success');
      toast.showToast();
    } catch (err) {
      toast.setMessage('There was an error saving the data.');
      toast.setSeverity('error');
      toast.showToast();
    }
  };

  const candidateWithSummary = {
    ...formattedCandidate,
    summary: candidate?.summary,
  };

  const refetchCandidateData = (): void => {
    refetchCandidate();
    allTechStacksResponse.refetch();
  };

  const isPageLoading =
    isLoadingCandidate ||
    allTechStacksResponse.isLoading ||
    allUsersResponse.isLoading ||
    candidateProfileResponse.isLoading;

  if (isPageLoading) {
    return <Loading fullPage />;
  }

  return (
    <CandidateInfoComponent
      refetchCandidateData={refetchCandidateData}
      workExperiences={workExperiences}
      expertises={expertises}
      candidateEducation={candidateEducation}
      techOptions={techStacksOptions}
      onSaveText={onSaveText}
      onScrollTo={onScrollToSmoothly}
      onSubmit={onSubmitHandler}
      questions={formattedQuestions}
      candidate={candidateWithSummary as any as EditableCandidate}
      setUpdatedField={setUpdatedField}
      updatedField={updatedField}
      onSubmitFromResume={onSubmitFromResume}
    />
  );
};

export default CandidateInfoPage;
